import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getAllUsers,
  toggleUserStatus,
  updateUser,
  reset,
} from "../../features/admin/adminSlice";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import Pagination from "../../components/shared/Pagination";

const Users = () => {
  const dispatch = useDispatch();
  const { users, isLoading, isError, isSuccess, message } = useSelector(
    (state) => state.admin,
  );
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    dispatch(getAllUsers());

    return () => {
      dispatch(reset());
    };
  }, [dispatch]);

  useEffect(() => {
    if (isError) {
      const msg = String(message || "");
      const suppress =
        /wallet summary/i.test(msg) ||
        /user not found in database/i.test(msg);
      if (!suppress) {
        toast.error(msg);
      }
    }
  }, [isError, message]);

  const handleToggleStatus = (userId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    dispatch(toggleUserStatus({ userId, newStatus }));
  };

  const handleRoleChange = async (userId, currentRole, newRole) => {
    if (currentRole === newRole) return;

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to change this user's role from ${currentRole} to ${newRole}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, change role!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await dispatch(
          updateUser({ id: userId, userData: { role: newRole } }),
        ).unwrap();
        toast.success("Role updated successfully!");
      } catch (error) {
        toast.error("Failed to update role. Please try again.");
      }
    }
  };

  // Filter users based on search term
  const allFilteredUsers =
    users?.filter(
      (user) =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  // Apply pagination
  const totalFilteredUsers = allFilteredUsers.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const filteredUsers = allFilteredUsers.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">User Management</h1>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="w-full md:w-1/3">
            <label htmlFor="search" className="sr-only">
              Search
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  ></path>
                </svg>
              </div>
              <input
                id="search"
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-md">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="w-full overflow-x-auto">
              <table className="min-w-[1200px] whitespace-nowrap">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-3 px-4 text-left">Name</th>
                  <th className="py-3 px-4 text-left">Email</th>
                  <th className="py-3 px-4 text-left">Phone</th>
                  <th className="py-3 px-4 text-left">Address</th>
                  <th className="py-3 px-4 text-left">City</th>
                  <th className="py-3 px-4 text-left">State</th>
                  <th className="py-3 px-4 text-left">Pincode</th>
                  <th className="py-3 px-4 text-left w-48">Role</th>
                  <th className="py-3 px-4 text-left">Total Rides</th>
                  <th className="py-3 px-4 text-left">Ratings</th>
                  <th className="py-3 px-4 text-left">Created At</th>
                  <th className="py-3 px-4 text-left">Status</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <tr key={user._id} className="border-t">
                      <td className="py-3 px-4">{user.name}</td>
                      <td className="py-3 px-4">{user.email}</td>
                      <td className="py-3 px-4">{user.phone || ""}</td>
                      <td className="py-3 px-4">{user.address || ""}</td>
                      <td className="py-3 px-4">{user.city || ""}</td>
                      <td className="py-3 px-4">{user.state || ""}</td>
                      <td className="py-3 px-4">{user.pincode || ""}</td>
                      <td className="py-3 px-4 w-48">
                        <select
                          className="block w-full min-w-[160px] py-1 px-2 border border-gray-300 bg-white rounded-md shadow-sm text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          value={user.role}
                          onChange={(e) =>
                            handleRoleChange(user._id, user.role, e.target.value)
                          }
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td className="py-3 px-4">{typeof user.totalRides === 'number' ? user.totalRides : (user.totalRides || 0)}</td>
                      <td className="py-3 px-4">{typeof user.ratings === 'number' ? user.ratings.toFixed(1) : (user.ratings || 0)}</td>
                      <td className="py-3 px-4">
                        {new Date(user.createdAt).toLocaleDateString("en-IN")}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.status === "active"
                              ? "bg-green-100 text-green-800"
                              : user.status === "blocked"
                                ? "bg-red-100 text-red-800"
                                : user.status === "inactive"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {user.status === "active"
                            ? "Active"
                            : user.status === "blocked"
                              ? "Blocked"
                              : user.status === "inactive"
                                ? "Inactive"
                                : user.status || "Inactive"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() =>
                            handleToggleStatus(user._id, user.status)
                          }
                          className={`px-3 py-1 rounded text-xs font-medium ${
                            user.status === "active"
                              ? "bg-red-100 text-red-800 hover:bg-red-200"
                              : "bg-green-100 text-green-800 hover:bg-green-200"
                          }`}
                        >
                          {user.status === "active" ? "Deactivate" : "Activate"}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      className="py-4 px-4 text-center text-gray-500"
                      colSpan="13"
                    >
                      {searchTerm
                        ? "No users matching search criteria"
                        : "No users found"}
                    </td>
                  </tr>
                )}
              </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 pb-4">
              <Pagination
                currentPage={currentPage}
                totalItems={totalFilteredUsers}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Users;
